import apiClient from "./axios";

/**
 * 邮件模板 API 服务
 */
export const emailTemplateApi = {
    /**
     * 获取邮件模板列表（分页）
     * @param {Object} params - 查询参数
     * @param {number} params.page - 页码
     * @param {number} params.page_size - 每页大小
     * @param {string} [params.category] - 模板分类
     * @param {string} [params.status] - 模板状态
     * @param {boolean} [params.is_public] - 是否公共模板
     * @param {string} [params.search] - 搜索关键词
     * @param {string} [params.tags] - 标签（逗号分隔）
     * @returns {Promise} API 响应
     */
    getTemplates: (params = {}) => {
        return apiClient.get("/v1/mail/templates", { params });
    },

    /**
     * 根据ID获取邮件模板详情
     * @param {number} templateId - 模板ID
     * @returns {Promise} API 响应
     */
    getTemplateById: (templateId) => {
        return apiClient.get(`/v1/mail/templates/${templateId}`);
    },

    /**
     * 根据模板代码获取邮件模板并解析收件人配置
     * @param {string} templateCode - 模板代码
     * @returns {Promise} API 响应，包含 resolved_to, resolved_cc, resolved_bcc 字段
     */
    getTemplateByCodeWithRecipients: (templateCode) => {
        return apiClient.get(`/v1/mail/templates/code/${templateCode}/with-recipients`);
    },

    /**
     * 创建邮件模板
     * @param {Object} templateData - 模板数据
     * @param {string} templateData.template_code - 模板代码
     * @param {string} templateData.name - 模板名称
     * @param {string} [templateData.description] - 模板描述
     * @param {string} templateData.category - 模板分类
     * @param {string} templateData.subject_template - 邮件主题模板
     * @param {string} templateData.body_html - HTML格式邮件正文模板
     * @param {string} [templateData.body_text] - 纯文本格式邮件正文模板
     * @param {Array} [templateData.content_blocks] - 内容块类型列表
     * @param {Object} [templateData.variables] - 变量定义
     * @param {Array} [templateData.tags] - 标签列表
     * @param {string} [templateData.status] - 模板状态
     * @param {boolean} [templateData.is_public] - 是否公共模板
     * @returns {Promise} API 响应
     */
    createTemplate: (templateData) => {
        return apiClient.post("/v1/mail/templates", templateData);
    },

    /**
     * 更新邮件模板
     * @param {number} templateId - 模板ID
     * @param {Object} templateData - 更新的模板数据
     * @param {string} [changeSummary] - 变更摘要
     * @returns {Promise} API 响应
     */
    updateTemplate: (templateId, templateData, changeSummary = null) => {
        const params = changeSummary ? { change_summary: changeSummary } : {};
        return apiClient.put(`/v1/mail/templates/${templateId}`, templateData, { params });
    },

    /**
     * 删除邮件模板（软删除）
     * @param {number} templateId - 模板ID
     * @returns {Promise} API 响应
     */
    deleteTemplate: (templateId) => {
        return apiClient.delete(`/v1/mail/templates/${templateId}`);
    },

    /**
     * 获取模板版本历史
     * @param {number} templateId - 模板ID
     * @returns {Promise} API 响应
     */
    getTemplateVersions: (templateId) => {
        return apiClient.get(`/v1/mail/templates/${templateId}/versions`);
    },

    /**
     * 获取所有模板分类
     * @returns {Promise} API 响应
     */
    getCategories: () => {
        return apiClient.get("/v1/mail/templates/categories");
    },

    /**
     * 获取所有可用标签
     * @returns {Promise} API 响应
     */
    getAvailableTags: () => {
        return apiClient.get("/v1/mail/templates/tags");
    },

    /**
     * 发送测试邮件
     * @param {Object} emailData - 邮件数据
     * @param {string} emailData.to_address - 收件人邮箱
     * @param {string} emailData.subject - 邮件主题
     * @param {string} emailData.body_html - HTML格式邮件正文
     * @returns {Promise} API 响应
     */
    sendTestEmail: (emailData) => {
        return apiClient.post("/v1/mail/send-test", emailData);
    },
};

/**
 * 邮件模板常量
 */
export const EMAIL_TEMPLATE_CONSTANTS = {
    // 模板状态
    STATUS: {
        ACTIVE: "ACTIVE",
        INACTIVE: "INACTIVE",
        DRAFT: "DRAFT",
    },

    // 模板分类
    CATEGORY: {
        FLIGHT_OPERATION: "FLIGHT_OPERATION",
        FUEL_REQUEST: "FUEL_REQUEST",
        CATERING_ORDER: "CATERING_ORDER",
        CREW_TRANSPORTATION: "CREW_TRANSPORTATION",
        HOTAC: "HOTAC",
        PERMIT_REQUEST: "PERMIT_REQUEST",
        GENERAL: "GENERAL",
    },

    // 内容块类型（与 EmailEditor 中的常量保持一致）
    CONTENT_BLOCK_TYPES: {
        FLIGHT_INFO: "flightInfo",
        ATTACHMENT: "attachment",
        CONTACT: "contact",
        CARGO_MANIFEST: "cargoManifest",
        CREW_INFO: "crewInfo",
        FUEL_INFO: "fuelInfo",
        CATERING_INFO: "cateringInfo",
        HOTAC_INFO: "hotacInfo",
        PERMIT_INFO: "permitInfo",
    },
};

export default emailTemplateApi;
